import React from "react";
import { useTranslation } from "react-i18next";
import { MoreVertical } from "lucide-react";
import type { ChatRoom } from "@/utils/chatFormat";
import {
  CHAT_TYPE_LABEL_KEYS,
  formatRoomTime,
  getRoomIcon,
  getRoomPeer,
  getRoomTitle,
  isGroupRoom,
} from "@/utils/chatFormat";
import ChatAvatar from "@/components/student/ChatAvatar";

interface ChatRoomRowProps {
  room: ChatRoom;
  currentUserId: string;
  language: string;
  onPress: (room: ChatRoom) => void;
  onManage?: (room: ChatRoom) => void;
}

/**
 * Chat list row — the web port of the RN `ChatContainer`: peer avatar with a
 * presence dot, the room name plus its type badge, the latest message preview
 * and, on the right, the message time and unread count.
 */
const ChatRoomRow: React.FC<ChatRoomRowProps> = ({
  room,
  currentUserId,
  language,
  onPress,
  onManage,
}) => {
  const { t } = useTranslation();

  const isGroup = isGroupRoom(room);
  const peer = getRoomPeer(room, currentUserId);
  const displayName = getRoomTitle(room, currentUserId) || t("Chat");
  const displayIcon = getRoomIcon(room, currentUserId);
  const latestMessage = room?.latest_message ?? null;
  const unreadCount = room?.unread_count ?? 0;
  const typeLabelKey = CHAT_TYPE_LABEL_KEYS[room?.type ?? ""];
  const isMine = latestMessage?.sender_id === currentUserId;
  const previewText = latestMessage?.message
    ? `${isMine ? `${t("You:")} ` : ""}${latestMessage.message}`
    : "";

  return (
    <article className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm transition hover:shadow-md">
      <button
        type="button"
        onClick={() => onPress(room)}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <ChatAvatar
          name={displayName}
          imageUrl={displayIcon}
          isOnline={isGroup ? null : (peer?.is_online ?? null)}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[15px] font-bold text-gray-900">
              {displayName}
            </span>
            {typeLabelKey && (
              <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500">
                {t(typeLabelKey)}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-[13px] text-gray-500">
            {previewText || t("No messages yet")}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span className="text-[11px] text-gray-400">
            {formatRoomTime(latestMessage?.ctime, language)}
          </span>
          {unreadCount > 0 && (
            <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#ffa217] px-1.5 text-[11px] font-bold text-white">
              {unreadCount > 99 ? "99+" : String(unreadCount)}
            </span>
          )}
        </div>
      </button>

      {onManage && (
        <button
          type="button"
          onClick={() => onManage(room)}
          aria-label={t("Manage Chat")}
          title={t("Manage Chat")}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-300 transition hover:bg-[#fff3e0] hover:text-[#ffa217]"
        >
          <MoreVertical size={16} />
        </button>
      )}
    </article>
  );
};

export default ChatRoomRow;
